import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type {
  AttendeeProfileResponse,
  GoingCountsResponse,
  MeetListResponse,
  RosterResponse,
} from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`).
 *
 * **Everything here needs a session.** `MEM-011` AC-1 is "any signed-in member", and `MEM-019`
 * AC-2 lists the roster and member profiles among the things that are not available signed out —
 * so unlike `event-discovery`'s two calls, none of these is unauthenticated-capable. `enabled` is
 * how a signed-out visitor is kept from firing a call that can only 401; it is a courtesy, and
 * the control is the server's.
 *
 * **No path carries a member id.** The reader is the session. The person being read *about* is
 * named by `attendeeRef`, which the API derives — the client never composes one.
 */

const rosterKey = (eventId: string): readonly unknown[] => [
  'attendee-preview',
  'roster',
  eventId,
];
const profileKey = (eventId: string, attendeeRef: string): readonly unknown[] => [
  'attendee-preview',
  'profile',
  eventId,
  attendeeRef,
];
const meetListKey = (eventId: string): readonly unknown[] => [
  'attendee-preview',
  'meet-list',
  eventId,
];

/**
 * **The going count on each Discover card** (`MEM-014` AC-2; Sumit's ruling of 2026-09-11).
 *
 * Asked only with a session — `enabled` is the courtesy, the member guard on the route is the
 * control — because the count is the roster module's attendance summary and `MEM-019` AC-2 keeps
 * it from a signed-out visitor. Keyed by the sorted ids, so a list re-rendering in the same order
 * asks once. An event the server left out has no count, and the card draws none.
 */
export function useGoingCounts(
  eventIds: ReadonlyArray<string>,
  options: { readonly enabled: boolean },
): UseQueryResult<GoingCountsResponse> {
  const ids = [...new Set(eventIds)].sort();
  return useQuery({
    queryKey: ['attendee-preview', 'going-counts', ids.join(',')],
    queryFn: () =>
      apiRequest<GoingCountsResponse>('/api/attendee-preview/going-counts', {
        searchParams: { eventIds: ids.join(',') },
      }),
    enabled: options.enabled && ids.length > 0,
    retry: false,
  });
}

/** `MEM-011` — the roster and the Who's coming module. */
export function useAttendeeRoster(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<RosterResponse> {
  return useQuery({
    queryKey: rosterKey(eventId),
    queryFn: () =>
      apiRequest<RosterResponse>(
        `/api/attendee-preview/events/${encodeURIComponent(eventId)}/attendees`,
      ),
    enabled: options.enabled && eventId.length > 0,
    retry: false,
  });
}

/**
 * `MEM-012` — one attendee, at whichever disclosure level the **server** decided.
 *
 * There is no `full`/`reduced` parameter and there must never be one: `MEM-012` AC-5 puts the gate
 * on the reader's registration, and a client-selectable level would be the gate moving into the
 * request.
 */
export function useAttendeeProfile(
  eventId: string,
  attendeeRef: string | null,
  options: { readonly enabled: boolean },
): UseQueryResult<AttendeeProfileResponse> {
  return useQuery({
    queryKey: profileKey(eventId, attendeeRef ?? ''),
    queryFn: () =>
      apiRequest<AttendeeProfileResponse>(
        `/api/attendee-preview/events/${encodeURIComponent(eventId)}/attendees/${encodeURIComponent(
          attendeeRef ?? '',
        )}`,
      ),
    enabled: options.enabled && eventId.length > 0 && (attendeeRef ?? '').length > 0,
    retry: false,
  });
}

/** `MEM-013` — the member's own list. `meetList: null` means "not a registrant". */
export function useMyMeetList(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<MeetListResponse> {
  return useQuery({
    queryKey: meetListKey(eventId),
    queryFn: () =>
      apiRequest<MeetListResponse>(
        `/api/attendee-preview/events/${encodeURIComponent(eventId)}/meet-list`,
      ),
    enabled: options.enabled && eventId.length > 0,
    retry: false,
  });
}

/**
 * `MEM-013` AC-2 — save, and its inverse.
 *
 * One mutation for both directions, taking the desired state rather than exposing an `add` and a
 * `remove`: the control is a toggle, and two hooks would let a screen call the wrong one from a
 * stale render. It **returns the whole list**, so every surface renders from the server's answer
 * rather than from an assumption about what its own tap did.
 *
 * The matches queries are invalidated alongside, and that is `MEM-025` AC-7: a card's saved state
 * and a profile's saved state are one entry, so a save from either must move both. Leaving the
 * match cache alone is how the two lists start to look different.
 */
export function useSetMeetListEntry(
  eventId: string,
): UseMutationResult<
  MeetListResponse,
  Error,
  { readonly attendeeRef: string; readonly saved: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attendeeRef, saved }) =>
      apiRequest<MeetListResponse>(
        `/api/attendee-preview/events/${encodeURIComponent(eventId)}/meet-list/${encodeURIComponent(
          attendeeRef,
        )}`,
        { method: saved ? 'PUT' : 'DELETE' },
      ),
    onSuccess: async (data) => {
      queryClient.setQueryData(meetListKey(eventId), data);
      await queryClient.invalidateQueries({ queryKey: ['attendee-preview', 'profile', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['matching'] });
    },
  });
}
